import { useCallback, useEffect, useRef, useState } from "react";

import { UpdateType } from "@/enum/list.enum";

import {
	CampaignStaleConflict,
	StoredCampaignEntry,
	isCampaignDeletedRefusal,
	isCampaignStructureLockedRefusal,
	readCampaignRevision,
	pickFreshestCampaignEntry,
	readCampaignStaleConflict,
} from "./campaignRevision.util";
import { useCampaignSaveQueue } from "./useCampaignSaveQueue";
import { canvasSnapshot } from "./canvasSnapshot.util";

import type {
	AutosaveReason,
	CampaignAutosave,
	CampaignSaveState,
	CampaignWriteOrigin,
	UseCampaignAutosaveArgs,
} from "./useCampaignAutosave.types";

const AUTOSAVE_DEBOUNCE_MS = 1000;

export type {
	AutosaveReason,
	CampaignAutosave,
	CampaignSaveState,
	CampaignSaveStatus,
	CampaignWriteOrigin,
	UseCampaignAutosaveArgs,
} from "./useCampaignAutosave.types";

/**
 * Autosave is bound to one campaign identity and revision (PREC-4026). Dirty graphs are never
 * silently replaced by a refresh. Settings owns a pause only after the canvas flush completes;
 * its HTTP acknowledgement releases that pause using the acknowledged graph and revision,
 * regardless of whether the socket/refetch has already arrived (PREC-4151, PREC-5000).
 */
export function useCampaignAutosave<TNode, TEdge>(
	args: UseCampaignAutosaveArgs<TNode, TEdge>
): CampaignAutosave {
	const { nodesRef, edgesRef, campaignUuid, seed, storedCampaign } = args;

	const latestRef = useRef(args);

	latestRef.current = args;

	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lastSavedRef = useRef<string | null>(null);
	const isInitializedRef = useRef(false);
	const isPausedRef = useRef(false);
	const mountedRef = useRef(true);
	const acknowledgedRef = useRef<StoredCampaignEntry | undefined>(undefined);
	const dirtySinceSeedRef = useRef(false);
	/** Adopt the next stored copy at or above this revision (after "load theirs"). */
	const adoptFromRevisionRef = useRef<number | null>(null);
	const seededRef = useRef<{ campaignUuid: string; revision: number }>({
		campaignUuid,
		revision: 0,
	});
	/** Mirrors `lastSavedAt` so callbacks can read it without re-creating on every save. */
	const lastSavedAtRef = useRef<number | null>(null);
	/**
	 * The last save was a change to the steps the structure lock refused (PREC-4616). The canvas is
	 * (or is about to be) the stored campaign again, so nothing is owed: the chip says why until the
	 * next edit, and a leave is never held up for the refused change.
	 */
	const structureRefusedRef = useRef(false);

	const [conflict, setConflict] = useState<CampaignStaleConflict | null>(null);
	const [saveState, setSaveState] = useState<CampaignSaveState>("idle");
	const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

	/** Back to rest: "Saved HH:MM" if this canvas has ever been written, otherwise nothing. */
	const markSettled = useCallback(() => {
		if (structureRefusedRef.current) {
			setSaveState("locked");

			return;
		}

		setSaveState(lastSavedAtRef.current === null ? "idle" : "saved");
	}, []);

	const snapshot = useCallback(
		(isValid: boolean) => canvasSnapshot(nodesRef.current, edgesRef.current, isValid),
		[nodesRef, edgesRef]
	);

	const isDirty = useCallback(
		() => snapshot(latestRef.current.isValid) !== lastSavedRef.current,
		[snapshot]
	);

	const clearTimer = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	};

	/** Copy the stored campaign into the canvas and make it the saved baseline. */
	const seedCanvas = useCallback(
		(revision: number, entry?: StoredCampaignEntry) => {
			const current = latestRef.current;

			const graph = entry ? current.hydrateCampaign(entry) : current.seed;

			nodesRef.current = graph?.initialNodes ?? current.fallbackNodes;
			edgesRef.current = graph?.initialEdges ?? [];
			lastSavedRef.current = snapshot(Boolean((entry ?? current.storedCampaign)?.isValid));
			seededRef.current = { campaignUuid: current.campaignUuid, revision };
			dirtySinceSeedRef.current = false;
		},
		[nodesRef, edgesRef, snapshot]
	);

	useEffect(() => {
		const incomingRevision = readCampaignRevision(storedCampaign);
		const hasStored = Boolean(seed?.initialNodes && storedCampaign);

		if (!isInitializedRef.current) {
			nodesRef.current = seed?.initialNodes ?? latestRef.current.fallbackNodes;
			edgesRef.current = seed?.initialEdges ?? [];

			if (hasStored) {
				seedCanvas(incomingRevision);
				isInitializedRef.current = true;
			}

			return;
		}

		if (campaignUuid !== seededRef.current.campaignUuid) {
			// The same page instance now shows another campaign: the canvas follows it, and any
			// save still pending for the previous campaign is dropped by the identity check.
			seedCanvas(incomingRevision);

			// Ownership, acknowledgements and conflicts belong to the campaign being left.
			isPausedRef.current = false;
			acknowledgedRef.current = undefined;
			adoptFromRevisionRef.current = null;
			structureRefusedRef.current = false;
			setConflict(null);
			lastSavedAtRef.current = null;
			setLastSavedAt(null);
			setSaveState("idle");

			return;
		}

		if (!hasStored) {
			return;
		}

		if (adoptFromRevisionRef.current !== null) {
			if (incomingRevision >= adoptFromRevisionRef.current) {
				adoptFromRevisionRef.current = null;
				seedCanvas(incomingRevision);
				isPausedRef.current = false;
				markSettled();
				latestRef.current.onCanvasReplaced();
			}

			return;
		}

		// A copy no newer than the one the canvas came from carries nothing to adopt — and a stale
		// one must never replace what the user typed since (the failure behind PREC-4026).
		if (incomingRevision <= seededRef.current.revision || isDirty()) {
			return;
		}

		seedCanvas(incomingRevision);
		latestRef.current.onCanvasReplaced();
	}, [seed, storedCampaign, campaignUuid, nodesRef, edgesRef, seedCanvas, isDirty, markSettled]);

	/**
	 * Put the stored campaign back on the canvas after the structure lock refused a save. Nothing was
	 * written, so the server holds the revision this canvas last saw: a cache copy at that revision is
	 * reloaded now, an older one when the refetch lands, with saving held until then so the refused
	 * steps are never sent again.
	 */
	const reloadAfterStructureRefusal = useCallback(() => {
		const current = latestRef.current;
		const serverRevision = seededRef.current.revision;
		const cachedRevision = readCampaignRevision(current.storedCampaign);

		clearTimer();
		structureRefusedRef.current = true;
		setSaveState("locked");

		if (cachedRevision >= serverRevision) {
			seedCanvas(cachedRevision);
			current.onCanvasReplaced();

			return;
		}

		isPausedRef.current = true;
		adoptFromRevisionRef.current = serverRevision;
	}, [seedCanvas]);

	const save = useCallback(
		async (origin: CampaignWriteOrigin, revisionOverride?: number) => {
			const current = latestRef.current;

			if (!mountedRef.current || seededRef.current.campaignUuid !== current.campaignUuid) {
				return;
			}

			const revision = revisionOverride ?? seededRef.current.revision;
			const stored = pickFreshestCampaignEntry(current.storedCampaign, acknowledgedRef.current);

			setSaveState("saving");

			try {
				const result = await current
					.updateList({
						id: current.listId,
						updateType: UpdateType.SETTINGS,
						campaignConfig: JSON.stringify({
							...current.campaignConfigMap,
							[current.campaignUuid]: {
								...stored,
								name: stored?.name ?? current.title,
								isValid: current.isValid,
								initialNodes: nodesRef.current,
								initialEdges: edgesRef.current,
								revision,
							},
						}),
						campaignIdToUpdate: current.campaignUuid,
						campaignWriteOrigin: origin,
					})
					.unwrap();

				if (!mountedRef.current || latestRef.current.campaignUuid !== current.campaignUuid) {
					return;
				}

				const entry = result?.campaignConfig?.[current.campaignUuid];
				const savedRevision = readCampaignRevision(entry);

				if (savedRevision > 0 && seededRef.current.campaignUuid === current.campaignUuid) {
					seededRef.current = { campaignUuid: current.campaignUuid, revision: savedRevision };
					acknowledgedRef.current = entry;
					current.onAcknowledged?.(entry);
				}

				lastSavedAtRef.current = Date.now();
				setLastSavedAt(lastSavedAtRef.current);
				setSaveState("saved");
			} catch (error) {
				if (!mountedRef.current || latestRef.current.campaignUuid !== current.campaignUuid) {
					return;
				}

				// The campaign was deleted (PREC-4258): nothing more is written from this canvas, and the
				// host leaves the campaign on "deleted".
				if (isCampaignDeletedRefusal(error, current.campaignUuid)) {
					isPausedRef.current = true;
					clearTimer();
					setSaveState("deleted");

					return;
				}

				// The steps cannot change any more (PREC-4616): the refused graph goes, the stored one
				// comes back, and text edits keep saving. The refused PATCH invalidated the list, so the
				// refetch also brings the lock that hides the structural controls.
				if (isCampaignStructureLockedRefusal(error, current.campaignUuid)) {
					reloadAfterStructureRefusal();

					return;
				}

				const staleConflict = readCampaignStaleConflict(error);

				// Let the next edit retry a save that failed for any other reason.
				lastSavedRef.current = null;

				if (!staleConflict || staleConflict.campaignId !== current.campaignUuid) {
					// Nothing else tells the user: the reject middleware redirects on rejected
					// queries only, so a refused mutation reaches them through this chip or not
					// at all (PREC-4151).
					setSaveState("error");

					return;
				}

				isPausedRef.current = true;

				if (dirtySinceSeedRef.current) {
					setSaveState("error");
					setConflict(staleConflict);
				} else {
					// Nothing of the user's is at stake — the newer copy is adopted silently.
					adoptFromRevisionRef.current = staleConflict.currentRevision;
					markSettled();
				}
			}
		},
		[nodesRef, edgesRef, markSettled, reloadAfterStructureRefusal]
	);

	const saveIfChanged = useCampaignSaveQueue({
		save,
		snapshot: () => snapshot(latestRef.current.isValid),
		lastSavedRef,
		isPausedRef,
		identity: () => latestRef.current.campaignUuid,
		markSettled,
	});

	const updateNow = useCallback(
		(reason: AutosaveReason = "edit") => {
			clearTimer();

			if (reason === "edit") {
				dirtySinceSeedRef.current = true;

				// A new edit on the reloaded canvas: the refusal has been said, this one saves anew.
				if (!isPausedRef.current) {
					structureRefusedRef.current = false;
				}
			}

			if (!isInitializedRef.current) {
				return;
			}

			setSaveState((state) =>
				isPausedRef.current && ["deleted", "locked", "error"].includes(state) ? state : "unsaved"
			);
			if (isPausedRef.current) {
				return;
			}

			timeoutRef.current = setTimeout(
				() => saveIfChanged("builder-autosave"),
				AUTOSAVE_DEBOUNCE_MS
			);
		},
		[saveIfChanged]
	);

	const flush = useCallback(async () => {
		clearTimer();

		if (isInitializedRef.current && !isPausedRef.current) {
			await saveIfChanged("builder-flush");
		}

		// Nothing left unsaved: either there was nothing to write, or the write was accepted. A
		// refused save clears `lastSavedRef`, so the canvas reads dirty again and this answers false
		// — which is what lets "Save and leave" keep the user here instead of losing their edit. A
		// change the structure lock refused is not owed: it can never be saved (PREC-4616).
		return structureRefusedRef.current || !isDirty();
	}, [saveIfChanged, isDirty]);

	const flushAndPause = useCallback(async () => {
		const identity = latestRef.current.campaignUuid;
		const saved = await flush();

		if (!mountedRef.current || !saved || identity !== latestRef.current.campaignUuid) {
			return false;
		}

		clearTimer();
		isPausedRef.current = true;

		return true;
	}, [flush]);

	const resumeAutoSave = useCallback(() => {
		if (!mountedRef.current || latestRef.current.campaignUuid !== campaignUuid) {
			return;
		}

		isPausedRef.current = false;
		if (isDirty()) {
			updateNow();
		}
	}, [campaignUuid, isDirty, updateNow]);

	// The HTTP acknowledgement owns this hand-off; a socket/refetch may arrive on either side.
	const resumeAutoSaveAfterSettings = useCallback(
		(entry: StoredCampaignEntry) => {
			if (!mountedRef.current || latestRef.current.campaignUuid !== campaignUuid) {
				return;
			}

			const current = latestRef.current;
			const stored = pickFreshestCampaignEntry(entry, current.storedCampaign)!;

			acknowledgedRef.current = stored;
			current.onAcknowledged?.(stored);
			// Settings can change validity; only a changed graph represents edits to preserve here.
			const saved = lastSavedRef.current;

			if (saved === null || snapshot(JSON.parse(saved).isValid) !== saved) {
				setConflict({
					campaignId: campaignUuid,
					currentRevision: readCampaignRevision(stored),
					incomingRevision: seededRef.current.revision,
					current: stored,
				});
				setSaveState("error");

				return;
			}

			seedCanvas(readCampaignRevision(stored), stored);
			isPausedRef.current = false;
			markSettled();
			current.onCanvasReplaced();
			if (isDirty()) {
				updateNow("validity");
			}
		},
		[campaignUuid, snapshot, seedCanvas, markSettled, isDirty, updateNow]
	);

	const retrySave = useCallback(() => {
		clearTimer();
		saveIfChanged("builder-flush");
	}, [saveIfChanged]);

	const keepMine = useCallback(async () => {
		if (!conflict) {
			return;
		}

		setConflict(null);
		isPausedRef.current = false;
		lastSavedRef.current = snapshot(latestRef.current.isValid);
		await save("builder-keep-mine", conflict.currentRevision);
	}, [conflict, save, snapshot]);

	const takeTheirs = useCallback(() => {
		if (!conflict) {
			return;
		}

		setConflict(null);
		dirtySinceSeedRef.current = false;

		const acknowledged = acknowledgedRef.current;

		if (
			readCampaignRevision(acknowledged) >= conflict.currentRevision ||
			readCampaignRevision(latestRef.current.storedCampaign) >= conflict.currentRevision
		) {
			const stored =
				readCampaignRevision(acknowledged) > readCampaignRevision(latestRef.current.storedCampaign)
					? acknowledged
					: undefined;

			seedCanvas(readCampaignRevision(stored ?? latestRef.current.storedCampaign), stored);
			isPausedRef.current = false;
			markSettled();
			latestRef.current.onCanvasReplaced();

			return;
		}

		// The refetch the refused save triggered has not landed yet; adopt it when it does.
		adoptFromRevisionRef.current = conflict.currentRevision;
		markSettled();
	}, [conflict, seedCanvas, markSettled]);

	useEffect(() => {
		mountedRef.current = true;

		return () => {
			mountedRef.current = false;
			clearTimer();
		};
	}, []);

	return {
		updateNow,
		flush,
		flushAndPause,
		resumeAutoSave,
		resumeAutoSaveAfterSettings,
		saveState,
		lastSavedAt,
		retrySave,
		conflict,
		keepMine,
		takeTheirs,
	};
}
